import { formatChemicalCompoundInTextAsUnicode } from "@omnia/shared/utils/format"
import { INtrReqConfig, NBreakevenConfig, NutrientGroup, ReqConfigMode } from "../../types"
import { ConfigDlg } from "./config"
import { ConfigVm } from "./config-vm"
import { sortDates } from "@omnia/shared/utils/utils"

export interface ConfigModeOptions {
    readonly mode: ReqConfigMode
    readonly terraMap: boolean
    merge?: boolean
    rounding?: number
}

export interface CalculationOptions {
    readonly ns: NutrientGroupOptions,
    readonly pk: NutrientGroupOptions,
    readonly lime: NutrientGroupOptions
}

export interface NutrientGroupOptions {

    readonly group: NutrientGroup
    readonly text: string
    readonly prefix: string
    readonly roundingOptions?: number[]

    readonly wholeField?: ConfigModeOptions
    readonly precision?: ConfigModeOptions
    readonly soilAnalysis?: ConfigModeOptions

}

interface NutrientGroupHeader {
    readonly def: GroupDefinition
    readonly text: string
    readonly roundingOptions?: number[]
}

interface CurrentOptions extends ConfigModeOptions {
    lastUpdate: Date
}

interface GroupDefinition {
    readonly group: NutrientGroup,
    readonly getDefaults: (mode: ReqConfigMode, terraMap: boolean) => ConfigModeOptions
}

const NsGroup: GroupDefinition = {
    group: NutrientGroup.NS,
    getDefaults: (mode, terraMap) => ({ ...getNsDefaults(mode), mode, terraMap })
}

const PkGroup: GroupDefinition = {
    group: NutrientGroup.PK,
    getDefaults: (mode, terraMap) => ({ ...getPkDefaults(mode, terraMap), mode, terraMap })
}

const LimeGroup: GroupDefinition = {
    group: NutrientGroup.Lime,
    getDefaults: (mode, terraMap) => ({ ...getLimeDefaults(mode), mode, terraMap })
}

export const cutOffOptions = (["P2O5", "K2O", "MgO"] as const).map(key => ({
    key,
    label: formatChemicalCompoundInTextAsUnicode(key),
    value: undefined as number
}))


export function applyOptions(cfg: INtrReqConfig[], options: CalculationOptions, nbreakeven: NBreakevenConfig, cutOffOptions: ConfigDlg["cutOffOptions"]) {

    const { ns, pk, lime } = options

    const cutOffObj = Object.fromEntries(cutOffOptions.map(o => [o.key, o.value]))

    for (const i of cfg) {

        applyGroupOptions(i, "N", ns)
        applyGroupOptions(i, "Pk", pk)
        applyGroupOptions(i, "Lime", lime)

        i.NBreakeven = nbreakeven

        i.P2O5CutOff = cutOffObj["P2O5"]
        i.K2OCutOff = cutOffObj["K2O"]
        i.MgOCutOff = cutOffObj["MgO"]


    }
}

function applyGroupOptions(cfg: INtrReqConfig, prefix: string, group: NutrientGroupOptions) {

    if (!group) return

    const mode = cfg[prefix + "ConfigMode"]
    const options = getOptionsForMode(group, mode)
    if (!options) return

    cfg[prefix + "MergeZones"] = options.merge
    cfg[prefix + "Rounding"] = options.rounding || 1

}

function getOptionsForMode(group: NutrientGroupOptions, mode: ReqConfigMode) {

    switch (mode) {
        case ReqConfigMode.WholeField: return group.wholeField
        case ReqConfigMode.SoilAnalysis: return group.soilAnalysis
        case ReqConfigMode.Precision: return group.precision
    }
}

export function setupOptions(cfg: ConfigVm[]) {

    const roundingOptions = [1, 5, 10, 15, 25]

    const ns = getNutrientGroupOptions({ def: NsGroup, text: "Nitrogen", roundingOptions }, cfg, "n")
    const pk = getNutrientGroupOptions({ def: PkGroup, text: "PK", roundingOptions }, cfg, "pk")
    const lime = getNutrientGroupOptions({ def: LimeGroup, text: "Lime" }, cfg, "lime")

    const options = { ns, pk, lime }

    const cutOff = getCutOffOptions(cfg)

    return { options, cutOff }

}

export function updateOptions(options: CalculationOptions, cfg: ConfigVm[]) {

    if (cfg.some(c => c.terraMap && (!c.lastUpdate))) {
        options.pk.soilAnalysis.merge = true
        options.pk.precision.merge = true
        options.lime.soilAnalysis.merge = true
        options.lime.precision.merge = true
    }

}


function getNutrientGroupOptions(hdr: NutrientGroupHeader, cfg: ConfigVm[], prefix: string): NutrientGroupOptions {

    const group = hdr.def.group
    const soilAnalysis = (group != NutrientGroup.NS)
    const wholeField = (group != NutrientGroup.Lime)

    const groupCfg = cfg.map(c => ({
        mode: c[prefix + "ConfigMode"],
        terraMap: c.terraMap,
        merge: c[prefix + "MergeZones"],
        rounding: c[prefix + "Rounding"],
        lastUpdate: c.lastUpdate
    }))

    return {
        group: group,
        text: hdr.text,
        prefix,
        roundingOptions: hdr.roundingOptions,
        wholeField: wholeField ? getModeOptions(hdr.def, groupCfg, ReqConfigMode.WholeField) : undefined,
        precision: getModeOptions(hdr.def, groupCfg, ReqConfigMode.Precision),
        soilAnalysis: soilAnalysis ? getModeOptions(hdr.def, groupCfg, ReqConfigMode.SoilAnalysis) : undefined
    }

}

function getModeOptions(def: GroupDefinition, cfg: CurrentOptions[], mode: ReqConfigMode): ConfigModeOptions {

    const matches = cfg
        .filter(i => i.mode == mode)
        .sort((a, b) => b.lastUpdate.valueOf() - a.lastUpdate.valueOf())

    if (matches.length) {
        const match = matches[0]
        return match
    }

    const terraMap = cfg.every(c => c.terraMap)

    return def.getDefaults(mode, terraMap)

}

function getNsDefaults(mode: ReqConfigMode) {

    if (mode == ReqConfigMode.Precision) {
        return { rounding: 10, merge: true }
    }

    return { rounding: 1 }

}

function getPkDefaults(mode: ReqConfigMode, terraMap: boolean) {

    if (terraMap || (mode == ReqConfigMode.Precision)) {
        return { rounding: 10, merge: true }
    }
    else if (mode == ReqConfigMode.SoilAnalysis) {
        return { rounding: 5, merge: false }
    }

    return { rounding: 1 }

}

function getLimeDefaults(mode: ReqConfigMode) {
    return { rounding: 1 }
}

function getCutOffOptions(cfg: ConfigVm[]) {

    cfg = cfg.filter(c => c.hasCutOffOptions())
    cfg = cfg.sort((a, b) => sortDates(b.lastUpdate, a.lastUpdate))

    const latest = cfg[0]

    const result = cutOffOptions.map(o => ({
        ...o,
        value: latest?.getCutOffValue(o.key)
    }))

    return result

}
